import React, { useMemo } from 'react';
import { formatCurrency } from '@/lib/portfolioCalculations';
import type { AssetsStats as AssetsStatsType } from '@/hooks/useAssetsUnifiedData';

interface AssetsSummaryHeaderProps {
  stats: AssetsStatsType;
  defaultCurrency?: string; // formatting only, no FX
}

export function AssetsSummaryHeader({ stats, defaultCurrency = 'CAD' }: AssetsSummaryHeaderProps) {
  const summary = useMemo(() => {
    const totalAssets = stats?.totalValue || 0;

    // Build currency list from stats.byCurrency values (case-insensitive, aggregated)
    const aggregated: Record<string, number> = {};
    Object.entries(stats?.byCurrency || {}).forEach(([ccy, obj]) => {
      const key = (ccy || '').trim().toUpperCase();
      const val = (obj?.value || 0);
      aggregated[key] = (aggregated[key] || 0) + val;
    });
    const entries = Object.entries(aggregated);

    const defaultCcy = (defaultCurrency || 'CAD').toUpperCase();
    entries.sort((a, b) => {
      if (a[0] === defaultCcy && b[0] !== defaultCcy) return -1;
      if (b[0] === defaultCcy && a[0] !== defaultCcy) return 1;
      return (b[1] || 0) - (a[1] || 0);
    });
    const limit = entries.length >= 3 ? 3 : 2;
    const top2 = entries.slice(0, limit);

    // Sources: manual vs connected (holdings + accounts combined)
    const manualCount = stats?.bySource?.manual?.count || 0;
    const connectedHoldings = stats?.bySource?.connected?.count || 0;
    const connectedAccounts = stats?.bySource?.account?.count || 0;
    const connectedCount = connectedHoldings + connectedAccounts;

    return { totalAssets, top2, manualCount, connectedCount };
  }, [stats, defaultCurrency]);

  const localeFor = (ccy: string) => {
    switch ((ccy || '').toUpperCase()) {
      case 'CAD': return 'en-CA';
      case 'USD': return 'en-US';
      case 'EUR': return 'en-IE';
      case 'GBP': return 'en-GB';
      case 'AUD': return 'en-AU';
      case 'NZD': return 'en-NZ';
      case 'JPY': return 'ja-JP';
      case 'CHF': return 'de-CH';
      default: return undefined;
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Assets total */}
      <div className="bg-card border rounded-lg p-4 shadow-sm min-w-0">
        <p className="text-sm font-medium text-muted-foreground mb-2">ASSETS</p>
        <div className="text-2xl font-bold tabular-nums text-green-600 whitespace-nowrap truncate">
          {formatCurrency(summary.totalAssets, (defaultCurrency || 'CAD'), { showDecimals: true })}
        </div>
      </div>

      {/* Currencies (bottom-aligned) */}
      <div className="bg-card border rounded-lg p-4 shadow-sm min-w-0">
        <div className="h-full flex flex-col justify-end gap-1">
          {summary.top2.map(([ccy, value]) => (
            <div key={ccy} className="text-sm flex items-baseline gap-2 min-w-0">
              <span className="w-10 text-muted-foreground font-mono">{ccy}</span>
              <span className="font-medium tabular-nums flex-1 min-w-0 text-right truncate">{formatCurrency(value, ccy as any, { showDecimals: true, locale: localeFor(ccy) })}</span>
            </div>
          ))}
          {summary.top2.length === 0 && (
            <div className="text-sm text-muted-foreground">No balances</div>
          )}
        </div>
      </div>

      {/* Sources (manual vs connected) */}
      <div className="bg-card border rounded-lg p-4 shadow-sm min-w-0">
        <p className="text-sm font-medium text-muted-foreground mb-2">SOURCES</p>
        <div className="text-sm">
          <div><span className="font-semibold">{summary.manualCount}</span> Manual</div>
          <div><span className="font-semibold">{summary.connectedCount}</span> Connected</div>
        </div>
      </div>
    </div>
  );
}

export default AssetsSummaryHeader;
